import React, { useEffect, useRef } from "react";
import { usePortfolioStore } from "../stores/portfolioStore";
import { Link, useLocation } from "@tanstack/react-router";
import ProcessSection from "./sections/ProcessSection";
import ConsultCTA from "./sections/ConsultCTA";
import LatestArticles from "./sections/LatestArticles";
import {
  useScrollReveal,
  useSkillBarReveal,
} from "../hooks/useScrollReveal";
import { scrollToElement } from "../lib/motion";
import { experience } from "../data/experience";
import { getAllArticles, type ArticleCategory } from "../lib/articles";

// Skills that have a matching article category (T3.4).
const SKILL_ARTICLE_CATEGORY: Record<string, ArticleCategory> = {
  React: "React",
  Go: "Go",
  PostgreSQL: "Data",
  AWS: "Cloud",
  Docker: "Cloud",
};

const ARTICLE_CATEGORIES_WITH_CONTENT = new Set(
  getAllArticles().map((article) => article.category)
);

const Home: React.FC = () => {
  const { skills } = usePortfolioStore();
  const location = useLocation();

  const processWrapRef = useRef<HTMLDivElement>(null);
  const backgroundRef = useRef<HTMLElement>(null);
  const skillsRef = useRef<HTMLDivElement>(null);

  useScrollReveal(backgroundRef);
  useSkillBarReveal(skillsRef);

  useEffect(() => {
    if (location.hash === "#about") {
      window.setTimeout(() => scrollToElement("#about"), 120);
    }
  }, [location.hash]);

  return (
    <div>
      {/* Process leads — client narrative first */}
      <div ref={processWrapRef}>
        <ProcessSection />
      </div>

      <ConsultCTA />

      {/* Background: experience + skills */}
      <section ref={backgroundRef} id="about" className="pb-12">
        <div data-reveal className="mb-6">
          <p className="text-cyan-400 font-mono text-sm tracking-widest uppercase mb-2">
            Background
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-white">
            Experience & skills
          </h2>
        </div>

        <div className="row pt-2">
          <div className="col-sm-6 md-top-1rem">
            <div className="experience-container" data-reveal>
              <h3 className="experience-title">
                <span className="fa fa-briefcase me-2" aria-hidden="true"></span>
                Professional Experience
              </h3>

              <div className="timeline">
                {experience.map((job) => (
                  <div
                    key={job.id}
                    className={`timeline-item${job.current ? " current" : ""}`}
                    data-reveal
                  >
                    <div
                      className={`timeline-marker${job.current ? " current-marker" : ""}`}
                      aria-hidden="true"
                    >
                      <span className={`fa fa-${job.icon}`}></span>
                    </div>
                    <div className="timeline-content">
                      <div
                        className={`experience-card${job.current ? " current-card" : ""}`}
                      >
                        <div className="experience-header">
                          <h4 className="job-title">{job.title}</h4>
                          <span className="company-name">{job.company}</span>
                          <span className="duration">{job.duration}</span>
                        </div>
                        <div className="experience-body">
                          {Array.isArray(job.body) ? (
                            <ul className="achievement-list">
                              {job.body.map((item) => (
                                <li key={item}>{item}</li>
                              ))}
                            </ul>
                          ) : (
                            <p>{job.body}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="col-sm-6" ref={skillsRef}>
            <div className="skills-container" data-reveal>
              <h3 className="text-info skills-title">
                <span className="fa fa-code me-2" aria-hidden="true"></span>
                Technical Skills
              </h3>

              {(["Front-end", "Back-end", "Deployment"] as const).map(
                (category) => (
                  <div
                    key={category}
                    className={`skill-category mb-4 ${category.toLowerCase().replace("-", "")}-category`}
                    data-reveal
                  >
                    <div className="category-header">
                      <h4 className="category-title">
                        <span
                          className={`fa ${
                            category === "Front-end"
                              ? "fa-desktop"
                              : category === "Back-end"
                                ? "fa-server"
                                : "fa-cloud"
                          } category-icon`}
                          aria-hidden="true"
                        ></span>
                        {category}
                      </h4>
                      <div className="category-line" aria-hidden="true"></div>
                    </div>
                    <div className="skills-grid">
                      {skills
                        .filter((skill) => skill.category === category)
                        .map((skill) => (
                          <div
                            key={skill.name}
                            className={`skill-card ${category.toLowerCase().replace("-", "")}-skill`}
                          >
                            <div className="skill-header">
                              <h6 className="skill-name">
                                {(() => {
                                  const articleCategory =
                                    SKILL_ARTICLE_CATEGORY[skill.name];
                                  return articleCategory &&
                                    ARTICLE_CATEGORIES_WITH_CONTENT.has(
                                      articleCategory
                                    ) ? (
                                    <Link
                                      to="/articles"
                                      search={{ category: articleCategory }}
                                      aria-label={`${skill.name} articles`}
                                    >
                                      {skill.name}
                                    </Link>
                                  ) : (
                                    skill.name
                                  );
                                })()}
                              </h6>
                            </div>
                            <div className="skill-progress">
                              <div className="progress-track">
                                <div
                                  className="progress-fill"
                                  data-skill-fill
                                  data-width={`${skill.percentage}%`}
                                  style={{ width: "0%" }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </section>

      <LatestArticles />
    </div>
  );
};

export default Home;
